import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Search, Loader2, Clock, TrendingUp, CheckCircle2, AlertTriangle,
  CalendarClock, ChevronRight, Headphones,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuthStore } from '../../stores/auth';
import { useCallStore } from '../../stores/callStore';

// ─── Types ──────────────────────────────────────────────────────────────

interface QueueLead {
  id: string;
  campaignId?: string;
  contactId: string;
  dealId?: string | null;
  callAttempts: number;
  lastCalledAt: string | null;
  priority?: number;
  campaign?: { id: string; name: string } | null;
  contact?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string | null;
    mobile?: string | null;
    account?: { name: string } | null;
  } | null;
}

interface TodayCall {
  id: string;
  status: string;
  duration: number;
  startTime: string | null;
  phoneNumber: string;
  disposition?: { id: string; name: string; color: string } | null;
  contact?: {
    firstName: string;
    lastName: string;
  } | null;
}

interface FollowUp {
  id: string;
  contactId: string;
  scheduledAt: string;
  notes?: string | null;
  priority?: string;
  contact?: {
    firstName: string;
    lastName: string;
    phone?: string;
  } | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function fmtTime(dt?: string | null): string {
  if (!dt) return '-';
  const d = new Date(dt);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtDate(dt?: string | null): string {
  if (!dt) return '-';
  const d = new Date(dt);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function fmtDur(sec: number): string {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function statusColor(s: string): string {
  if (s === 'CONNECTED') return 'bg-green-50 text-green-700';
  if (s === 'MISSED' || s === 'NO_ANSWER') return 'bg-red-50 text-red-700';
  if (s === 'IN_PROGRESS' || s === 'RINGING') return 'bg-amber-50 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

// ─── Page ───────────────────────────────────────────────────────────────

export function TelecallerTodayPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const dial = useCallStore((s) => s.dial);
  const dialLoading = useCallStore((s) => s.dialLoading);

  const [queue, setQueue] = useState<QueueLead[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [todayCalls, setTodayCalls] = useState<TodayCall[]>([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [target] = useState(30); // Default MVP target

  // ── Load queue ──
  const loadQueue = async () => {
    try {
      const res = await api.get('/telecaller/queue', { params: { limit: 25 } });
      setQueue(res.data?.data ?? []);
    } catch {
      setQueue([]);
    } finally {
      setQueueLoading(false);
    }
  };

  // ── Load today's calls for me ──
  const loadCalls = async () => {
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const params: Record<string, string> = {
        limit: '100',
        page: '1',
        startDate: start,
        endDate: now.toISOString(),
      };
      if (user?.id) params.agentId = user.id;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/telecaller/calls', { params });
      setTodayCalls(res.data?.data ?? []);
    } catch {
      setTodayCalls([]);
    } finally {
      setCallsLoading(false);
    }
  };

  // ── Load follow-ups due today ──
  const loadFollowUps = async () => {
    try {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
      const res = await api.get('/telecaller/followups', {
        params: { status: 'PENDING', endDate: endOfDay, limit: 25 },
      });
      setFollowUps(res.data?.data ?? []);
    } catch {
      setFollowUps([]);
    }
  };

  // ── Load credit balance ──
  const loadCredits = async () => {
    try {
      const res = await api.get('/credits/balance');
      const bal = res.data?.data?.balance;
      if (typeof bal === 'number') setCreditBalance(bal);
    } catch {
      // Non-critical
    }
  };

  // Initial + polling
  useEffect(() => {
    loadQueue();
    loadCalls();
    loadFollowUps();
    loadCredits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => {
      loadQueue();
      loadCalls();
    }, 30000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => loadFollowUps(), 60000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCalls();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived: filtered queue by search ──
  const filteredQueue = useMemo(() => {
    if (!search.trim()) return queue;
    const q = search.toLowerCase();
    return queue.filter((l) => {
      const name = l.contact ? `${l.contact.firstName} ${l.contact.lastName}`.toLowerCase() : '';
      const phone = l.contact?.phone || l.contact?.mobile || '';
      const campaign = l.campaign?.name.toLowerCase() || '';
      return name.includes(q) || phone.includes(q) || campaign.includes(q);
    });
  }, [queue, search]);

  // ── Stats ──
  const stats = useMemo(() => {
    const connected = todayCalls.filter((c) => c.status === 'CONNECTED');
    const totalDur = connected.reduce((a, c) => a + (c.duration || 0), 0);
    const avgTalk = connected.length ? Math.round(totalDur / connected.length) : 0;
    const connectRate = todayCalls.length
      ? Math.round((connected.length / todayCalls.length) * 100)
      : 0;
    return {
      total: todayCalls.length,
      connected: connected.length,
      connectRate,
      avgTalk,
      pendingFollowUps: followUps.length,
    };
  }, [todayCalls, followUps]);

  // ── Dial handler ──
  const onDial = async (lead: QueueLead) => {
    const phone = lead.contact?.phone || lead.contact?.mobile || '';
    const active = await dial(lead.id, phone);
    if (active?.callId) {
      navigate(`/telecaller/call/${active.callId}`);
    }
  };

  // ── Credit low banner ──
  const creditsLow = creditBalance !== null && creditBalance < 20;

  return (
    <div className="pb-20"> {/* pb-20 to clear CallBar */}
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Today</h1>
          <p className="text-sm text-[var(--text-muted)]">Your agent cockpit</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] px-4 py-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--text-muted)]">
              <Headphones size={12} /> Shift target
            </div>
            <div className="mt-0.5 text-lg font-bold tabular-nums text-[var(--text-primary)]">
              {stats.connected} <span className="text-sm font-normal text-[var(--text-muted)]">/ {target}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Credit banner */}
      {creditsLow && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={16} />
          <span>
            Credits low — <strong>{creditBalance}</strong> remaining. Contact admin to top up.
          </span>
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search queue by name, phone or campaign..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">All statuses</option>
          <option value="CONNECTED">Connected</option>
          <option value="NO_ANSWER">Missed</option>
          <option value="BUSY">Busy</option>
          <option value="FAILED">Failed</option>
        </select>
      </div>

      {/* 3-panel grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Queue panel */}
        <section className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
          <header className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Queue</h2>
            <span className="text-xs text-[var(--text-muted)]">{filteredQueue.length} leads</span>
          </header>
          <div className="max-h-[calc(100vh-260px)] min-h-[300px] overflow-y-auto">
            {queueLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : filteredQueue.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                No leads in queue
              </p>
            ) : (
              filteredQueue.map((lead) => {
                const name = lead.contact
                  ? `${lead.contact.firstName} ${lead.contact.lastName}`.trim()
                  : 'Unknown';
                const phone = lead.contact?.phone || lead.contact?.mobile || '-';
                return (
                  <button
                    key={lead.id}
                    onClick={() => onDial(lead)}
                    disabled={dialLoading}
                    className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--saffron-50)] text-[var(--ink-0)]">
                      <Phone size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">{name}</div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500">
                        <span>{phone}</span>
                        {lead.campaign && (
                          <>
                            <span>·</span>
                            <span className="truncate">{lead.campaign.name}</span>
                          </>
                        )}
                      </div>
                      {lead.lastCalledAt && (
                        <div className="text-[10px] text-gray-400">
                          Last: {fmtDate(lead.lastCalledAt)} {fmtTime(lead.lastCalledAt)} · {lead.callAttempts} attempts
                        </div>
                      )}
                    </div>
                    <ChevronRight size={14} className="text-gray-300" />
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* Today's calls panel */}
        <section className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
          <header className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Today's calls</h2>
            <span className="text-xs text-[var(--text-muted)]">{todayCalls.length}</span>
          </header>
          <div className="max-h-[calc(100vh-260px)] min-h-[300px] overflow-y-auto">
            {callsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : todayCalls.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                No calls yet today
              </p>
            ) : (
              todayCalls.map((c) => {
                const name = c.contact
                  ? `${c.contact.firstName} ${c.contact.lastName}`.trim() || c.phoneNumber
                  : c.phoneNumber;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/telecaller/call/${c.id}`)}
                    className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900">{name}</div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500">
                        <span>{fmtTime(c.startTime)}</span>
                        <span>·</span>
                        <span>{fmtDur(c.duration)}</span>
                        {c.disposition && (
                          <>
                            <span>·</span>
                            <span
                              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                              style={{ backgroundColor: c.disposition.color + '20', color: c.disposition.color }}
                            >
                              {c.disposition.name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColor(c.status)}`}>
                      {c.status.replace(/_/g, ' ')}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* Stats panel */}
        <section className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
          <header className="border-b border-[var(--border-primary)] px-4 py-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Quick stats</h2>
          </header>
          <div className="grid grid-cols-2 gap-3 p-4">
            <StatCard label="Calls today" value={stats.total} icon={<Phone size={14} />} />
            <StatCard label="Connect rate" value={`${stats.connectRate}%`} icon={<TrendingUp size={14} />} />
            <StatCard label="Avg talk time" value={fmtDur(stats.avgTalk)} icon={<Clock size={14} />} />
            <StatCard label="Follow-ups" value={stats.pendingFollowUps} icon={<CalendarClock size={14} />} />
          </div>
          <div className="border-t border-[var(--border-primary)] px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)]">
              <CheckCircle2 size={13} className="text-emerald-500" />
              Shift progress
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full bg-[var(--ink-0)] transition-all"
                style={{ width: `${Math.min(100, (stats.connected / target) * 100)}%` }}
              />
            </div>
            <div className="mt-1 text-[11px] text-[var(--text-muted)]">
              {stats.connected} of {target} connected
            </div>
          </div>
        </section>
      </div>

      {/* Follow-ups tab */}
      <section className="mt-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        <header className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-[var(--ink-0)]" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Follow-ups due today</h2>
          </div>
          <span className="text-xs text-[var(--text-muted)]">{followUps.length}</span>
        </header>
        <div>
          {followUps.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">
              No follow-ups pending for today
            </p>
          ) : (
            followUps.map((f) => {
              const name = f.contact
                ? `${f.contact.firstName} ${f.contact.lastName}`.trim() || 'Unknown'
                : 'Unknown';
              return (
                <button
                  key={f.id}
                  onClick={() => navigate(`/contacts/${f.contactId}`)}
                  className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{name}</div>
                    <div className="text-[11px] text-gray-500">
                      {fmtTime(f.scheduledAt)}
                      {f.notes ? ` · ${f.notes}` : ''}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-gray-300" />
                </button>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {icon} {label}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums text-gray-900">{value}</div>
    </div>
  );
}
